import i18n from "i18next";
import { useTranslation, initReactI18next } from "react-i18next";
import { RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./App.css";
import iconCross from "./photos/cross-black2.svg";
import AutoExpandingTextArea from "./Components/AutoExpandingTextArea.tsx";

interface AIRoomSetupProps {
  roomName: string;
  setRoomState: (state: string) => void;
  fetchString: string;
  socket: any;
  setAIRoomState: (state: string) => void;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  showInfoMessage: (
    type: string,
    message: string,
    isAdd: boolean,
    duration?: string,
    isDelay?: boolean,
  ) => void;
}

interface AIRoomSettings {
  names: string[];
  craziness: number;
  sexualScale: number;
  drinkAmount: number;
  freeContext: string;
}

enum Step {
  One,
  Two,
  Three,
  Four,
}

interface NameStepProps {
  names: string[];
  textArea: RefObject<HTMLTextAreaElement>;
  nameInput: string;
  setNameInput: (value: string) => void;
  addPlayer: (name: string) => void;
  removePlayer: (name: string) => void;
  namesContainerRef: RefObject<HTMLDivElement>;
}

const NameStep: React.FC<NameStepProps> = ({
  names,
  textArea,
  nameInput,
  setNameInput,
  addPlayer,
  removePlayer,
  namesContainerRef,
}) => {
  return (
    <div className="App-header">
      <div style={{ marginTop: 32 }}>
        <p className="text-h2 text-color-black" style={{ marginBottom: 24 }}>
          LEGG TIL SPILLERE
        </p>
        <div className="names-container" ref={namesContainerRef}>
          {names.map((name, index) => (
            <div
              key={index}
              className="name-box"
              onClick={() => removePlayer(name)}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <p className="text-h4">{name}</p>
                <img src={iconCross} alt="Remove icon" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="container" style={{ marginBottom: 16 }}>
        <input
          className="button-primary shadow-black text-h3 bottom-margin-l"
          autoComplete="off"
          type="text"
          maxLength={20}
          value={nameInput}
          onChange={(e) => setNameInput(e.currentTarget.value)}
          placeholder={"SKRIV NAVN..."}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault(); // prevent new line
              addPlayer(nameInput);
              setNameInput("");
            }
          }}
          onBlur={() => {
            addPlayer(nameInput);
            setNameInput("");
          }}
        ></input>

        <div
          className="button-primary button-text-l shadow-black text-color-black box-button-center"
          onClick={() => addPlayer(nameInput)}
        >
          LEGG TIL
        </div>
      </div>
    </div>
  );
};

interface SelectProps {
  label: string;
  value: number;
  current: number;
  onSelect: (value: number) => void;
}

export const Select: React.FC<SelectProps> = ({
  label,
  value,
  current,
  onSelect,
}) => {
  return (
    <div
      className="button-tertiary-m button-text-s text-color-black white-fill"
      onClick={() => onSelect(value)}
      style={{
        border: `4px solid ${current === value ? "#F42272" : "transparent"}`,
        padding: "5px 12px",
        display: "flex",
        alignItems: "start",
        cursor: "pointer",
      }}
    >
      {label}
    </div>
  );
};

interface CrazinessAndSexualScaleStepProps {
  craziness: number;
  applyCraziness: (craziness: number) => void;
  sexualScale: number; // 0–100
  setSexualScale: (n: number) => void;
  applySexualScale: (sexualScale: number) => void;
}

const CrazinessAndSexualScaleStep: React.FC<
  CrazinessAndSexualScaleStepProps
> = ({
  craziness,
  applyCraziness,
  sexualScale,
  setSexualScale,
  applySexualScale,
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <div style={{ marginTop: 32 }}>
        <p className="text-h2 text-color-black" style={{ marginBottom: 24 }}>
          STEMNINGSRAPPORT
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Select
            label="ROLIG OPPVARMING"
            value={1}
            current={craziness}
            onSelect={applyCraziness}
          />
          <Select
            label="GODE I GASSEN"
            value={2}
            current={craziness}
            onSelect={applyCraziness}
          />
          <Select
            label="FYLLAAAAAAAAAA"
            value={3}
            current={craziness}
            onSelect={applyCraziness}
          />
          <Select
            label="CAN'T STAND"
            value={4}
            current={craziness}
            onSelect={applyCraziness}
          />
        </div>
      </div>

      {/* Sexuality slider */}
      <div style={{ marginTop: 64 }}>
        <p className="text-h2 text-color-black" style={{ marginBottom: 24 }}>
          SEKSUELL SKALA
        </p>
        <input
          style={{ padding: "0px 6px 0px 6px" }}
          className="square-slider"
          type="range"
          min={0}
          max={100}
          step={1}
          value={sexualScale}
          onChange={(e) => setSexualScale(Number(e.target.value))} // live preview
          onMouseUp={(e) => applySexualScale(Number(e.currentTarget.value))}
          onTouchEnd={(e) => applySexualScale(Number(e.currentTarget.value))}
        />
      </div>
    </div>
  );
};

interface DrinkAmountStepProps {
  drinkAmount: number;
  applyDrinkAmount: (drinkAmount: number) => void;
}

const DrinkAmountStep: React.FC<DrinkAmountStepProps> = ({
  drinkAmount,
  applyDrinkAmount,
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <div style={{ marginTop: 32 }}>
        <p
          className="text-h2 text-color-black"
          style={{ marginBottom: 24, lineHeight: 1.1 }}
        >
          HVOR MYE VIL DERE DRIKKE?
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Select
            label="ROLIG DRIKKING"
            value={1}
            current={drinkAmount}
            onSelect={applyDrinkAmount}
          />
          <Select
            label="VANLIG NORK VORS"
            value={2}
            current={drinkAmount}
            onSelect={applyDrinkAmount}
          />
          <Select
            label="HARDE PÅ FLASKA"
            value={3}
            current={drinkAmount}
            onSelect={applyDrinkAmount}
          />
          <Select
            label="CHUGLIFE"
            value={4}
            current={drinkAmount}
            onSelect={applyDrinkAmount}
          />
        </div>
      </div>
    </div>
  );
};

interface FreeContextStepProps {
  freeContext: string;
  setFreeContext: (value: string) => void;
  applyFreeContext: (value: string) => void;
}

const FreeContextStep: React.FC<FreeContextStepProps> = ({
  freeContext,
  setFreeContext,
  applyFreeContext,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "auto",
        paddingRight: 10,
        paddingBottom: 10,
        marginRight: -10,
      }}
    >
      <p
        className="text-h2 text-color-black"
        style={{ marginTop: 32, marginBottom: 24 }}
      >
        FRITT INPUT
      </p>
      <AutoExpandingTextArea
        value={freeContext}
        onChange={setFreeContext}
        textareaRef={textAreaRef}
        placeholder={"SKRIV HER ..."}
        maxLength={280}
        onBlur={() => applyFreeContext(freeContext)}
      />
    </div>
  );
};

function AIRoomSetup({
  roomName,
  setRoomState,
  fetchString,
  socket,
  setAIRoomState,
  menuOpen,
  setMenuOpen,
  showInfoMessage,
}: AIRoomSetupProps) {
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState<AIRoomSettings>({
    names: [],
    craziness: 0,
    sexualScale: 0,
    drinkAmount: 0,
    freeContext: "",
  });

  const [nameInput, setNameInput] = useState<string>("");

  const [step, setStep] = useState<Step>(Step.One);
  const textArea = useRef<HTMLTextAreaElement | null>(null);

  const [doneSettingUp, setDoneSettingUp] = useState<boolean>(false);

  const [settingsChanged, setSettingsChanged] = useState<boolean>(false);

  const namesContainerRef = useRef<HTMLDivElement>(null);
  const shouldScrollToBottomRef = useRef(false);

  const getSettings = () => {
    fetch(fetchString + roomName, {
      headers: {
        Accept: "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const fetchedSettings: AIRoomSettings = data["data"][2];
        setSettings(fetchedSettings);

        setDoneSettingUp(data["data"][3]);
      });
  };

  useEffect(() => {
    getSettings();
  }, []);

  const addPlayer = (raw: string) => {
    const name = raw.trim();
    if (!name) return;

    shouldScrollToBottomRef.current = true;

    const el = namesContainerRef.current;
    if (el) {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth",
      });
    }

    setSettings((prev) => ({
      ...prev,
      names: [...prev.names, name],
    }));

    // fire-and-forget to server
    socket.emit("ai room add player", { roomName, name });
    setSettingsChanged(true);
  };

  const removePlayer = (raw: string) => {
    const name = raw.trim();
    if (!name) return;

    shouldScrollToBottomRef.current = false;

    setSettings((prev) => {
      const index = prev.names.indexOf(name);
      if (index === -1) return prev; // name not found

      // create a new array without the first matching occurrence
      const updated = [...prev.names];
      updated.splice(index, 1);

      return {
        ...prev,
        names: updated,
      };
    });

    // fire-and-forget to server
    socket.emit("ai room remove player", { roomName, name });
    setSettingsChanged(true);
  };

  useLayoutEffect(() => {
    if (!shouldScrollToBottomRef.current) return;

    const el = namesContainerRef.current;
    if (!el) return;

    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });

    // reset so other updates don't scroll
    shouldScrollToBottomRef.current = false;
  }, [settings.names]);

  const applyCraziness = (craziness: number) => {
    setSettings((prev) => ({
      ...prev,
      craziness,
    }));

    // fire-and-forget to server
    socket.emit("ai room set craziness", { roomName, craziness });
    setSettingsChanged(true);
  };

  const applySexualScale = (sexualScale: number) => {
    setSettings((prev) => ({
      ...prev,
      sexualScale,
    }));

    // fire-and-forget to server
    socket.emit("ai room set sexual scale", { roomName, sexualScale });
    setSettingsChanged(true);
  };

  const applyDrinkAmount = (drinkAmount: number) => {
    setSettings((prev) => ({
      ...prev,
      drinkAmount,
    }));

    // fire-and-forget to server
    socket.emit("ai room set drink amount", { roomName, drinkAmount });
    setSettingsChanged(true);
  };

  const applyFreeContext = (value: string) => {
    setSettings((prev) => ({
      ...prev,
      freeContext: value,
    }));

    // fire-and-forget to server
    socket.emit("ai room set free context", { roomName, freeContext: value });
    setSettingsChanged(true);
  };

  const setRoomSetupDone = () => {
    if (doneSettingUp) return;
    socket.emit("ai room set setup done", { roomName });
    setSettingsChanged(true);
  };

  const next = () => {
    if (step === Step.One && settings.names.length <= 1) {
      showInfoMessage("warning", "Legg til minst 2 spillere", false);
      return;
    }
    if (step < Step.Four) setStep(step + 1);
    if (step === Step.Four) {
      if (!doneSettingUp) {
        setRoomSetupDone();
      }
      setAIRoomState("done");
      setMenuOpen(false);
      if (settingsChanged) {
        socket.emit("ai room settings changed", { roomName });
      }
    }
  };

  function backButton() {
    if (step === Step.One) {
      if (menuOpen) {
        if (step === Step.One && settings.names.length <= 1) {
          showInfoMessage("warning", "Legg til minst 2 spillere", false);
          return;
        }
        setMenuOpen(false);
        setAIRoomState("done");
        if (settingsChanged) {
          socket.emit("ai room settings changed", { roomName });
        }
      } else setRoomState("start screen");
    } else if (step > Step.One) setStep(step - 1);
  }

  const topBarRender = (): JSX.Element => (
    <div className="container-row lift-topbar">
      <div className="container-row-start">
        <div
          className="button-tertiary-m button-text-m text-color-black"
          onClick={backButton}
          style={{ maxHeight: "32.5px" }}
        >
          {t("back")}
        </div>
      </div>
      <div className="text-h3 text-color-black left-margin-s text-force-break">
        {roomName}
      </div>
    </div>
  );

  //   If settings not defined send to setting
  return (
    <div className="App-header">
      {topBarRender()}
      {step === 0 && (
        <NameStep
          names={settings.names}
          textArea={textArea}
          nameInput={nameInput}
          setNameInput={setNameInput}
          addPlayer={addPlayer}
          removePlayer={removePlayer}
          namesContainerRef={namesContainerRef}
        />
      )}
      {step === 1 && (
        <CrazinessAndSexualScaleStep
          craziness={settings.craziness}
          applyCraziness={applyCraziness}
          sexualScale={settings.sexualScale}
          setSexualScale={(sexualScale) =>
            setSettings((prev) => ({ ...prev, sexualScale }))
          }
          applySexualScale={applySexualScale}
        />
      )}
      {step === 2 && (
        <DrinkAmountStep
          drinkAmount={settings.drinkAmount}
          applyDrinkAmount={applyDrinkAmount}
        />
      )}
      {step === 3 && (
        <FreeContextStep
          freeContext={settings.freeContext}
          setFreeContext={(freeContext) =>
            setSettings((prev) => ({ ...prev, freeContext }))
          }
          applyFreeContext={applyFreeContext}
        />
      )}

      <div
        className="button-primary black-fill button-text-l text-color-pink shadow-pink box-button-center"
        onClick={next}
      >
        {step === 3
          ? doneSettingUp
            ? "SPILL VIDERE"
            : "GENERER ROM"
          : "NESTE"}
      </div>
    </div>
  );
}

export default AIRoomSetup;
